import { useQuery } from '@tanstack/react-query';
import Modal from './Modal';
import LoadingSpinner from './LoadingSpinner';
import { vendorApi } from '../services/vendor';
import type { UploadListItem, ParsedEventListItem } from '../types';

interface Props {
  upload: UploadListItem | null;
  onClose: () => void;
}

function GuardMatchBadge({ event }: { event: ParsedEventListItem }) {
  if (event.guard_id && event.guard_name) {
    const conf = event.guard_match_confidence ? parseFloat(event.guard_match_confidence) : null;
    const isHigh = conf === null || conf >= 0.85;
    return (
      <div className="flex flex-col">
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${
          isHigh ? 'text-emerald-700' : 'text-amber-700'
        }`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${
            isHigh ? 'bg-emerald-500' : 'bg-amber-500'
          }`} />
          {event.guard_name}
        </span>
        {conf !== null && (
          <span className="text-[10px] text-slate-400">
            {isHigh ? 'Matched' : 'Low confidence'} · {Math.round(conf * 100)}%
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-500">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400" />
        Unmatched
      </span>
      {event.guard_name_raw && (
        <span className="text-[10px] text-slate-400 truncate max-w-[140px]" title={event.guard_name_raw}>
          "{event.guard_name_raw}"
        </span>
      )}
    </div>
  );
}

function severityDot(level: number) {
  if (level >= 4) return 'bg-red-500';
  if (level === 3) return 'bg-amber-500';
  if (level === 2) return 'bg-yellow-400';
  return 'bg-slate-300';
}

export default function UploadEventsModal({ upload, onClose }: Props) {
  const isOpen = !!upload;

  // 'partial' still produced ParsedEvent rows (some events parsed,
  // some failed) — fetch them so the vendor can review what did land.
  const hasEvents = upload?.status === 'completed' || upload?.status === 'partial';
  const { data, isLoading, isError } = useQuery({
    queryKey: ['vendor', 'upload-events', upload?.id],
    queryFn: () => vendorApi.getUploadEvents(upload!.id, { page_size: '200' }).then((r) => r.data),
    enabled: isOpen && hasEvents,
  });

  const events = data?.results ?? [];

  // Guard-match summary counts (drive the header panel).
  let matched = 0, lowConf = 0, unmatched = 0;
  for (const e of events) {
    if (!e.guard_id) unmatched++;
    else if (e.guard_match_confidence && parseFloat(e.guard_match_confidence) < 0.85) lowConf++;
    else matched++;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={upload?.file_name} size="xl">
      {!upload ? null : !hasEvents ? (
        <div className="text-center py-10">
          <p className="text-sm text-slate-600">
            {upload.status === 'queued' && 'This upload is queued for processing. Events will appear once parsing finishes.'}
            {upload.status === 'processing' && 'This upload is still being parsed. Check back in a moment.'}
            {upload.status === 'failed' && (
              <>
                Parsing failed. {'error_message' in upload && (upload as { error_message?: string }).error_message}
              </>
            )}
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-10"><LoadingSpinner /></div>
      ) : isError ? (
        <div className="text-center py-10">
          <p className="text-sm text-red-600">Failed to load parsed events.</p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-sm text-slate-500">No events were parsed from this upload.</p>
        </div>
      ) : (
        <>
          {upload.status === 'partial' && (
            <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-800">
                Parsing completed with issues — some events may be missing from this report.
              </p>
            </div>
          )}

          {/* Guard attribution summary */}
          <div className="mb-4 grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500 uppercase">Matched</p>
              <p className="mt-1 text-xl font-bold text-emerald-600">{matched}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500 uppercase">Low confidence</p>
              <p className="mt-1 text-xl font-bold text-amber-600">{lowConf}</p>
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-3">
              <p className="text-xs font-medium text-slate-500 uppercase">Unmatched</p>
              <p className="mt-1 text-xl font-bold text-slate-500">{unmatched}</p>
            </div>
          </div>

          {(unmatched > 0 || lowConf > 0) && (
            <p className="mb-3 text-xs text-slate-500">
              Unmatched entries usually mean the guard name in the report doesn't match a guard on your roster.
              Add the guard (or correct the spelling) so future uploads attribute correctly.
            </p>
          )}

          {/* Events table */}
          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Time</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Activity</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Guard</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Severity</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {events.map((e) => (
                  <tr key={e.id} className="align-top">
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">
                      {new Date(e.event_timestamp).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{e.activity_type_normalized}</div>
                      {e.activity_type_raw && e.activity_type_raw.toLowerCase() !== e.activity_type_normalized.toLowerCase() && (
                        <div className="text-xs text-slate-400 truncate max-w-[160px]" title={e.activity_type_raw}>
                          {e.activity_type_raw}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2"><GuardMatchBadge event={e} /></td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{e.patrol_location || '—'}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`inline-block w-2 h-2 rounded-full ${severityDot(e.severity_level)}`} />
                        <span className="text-xs text-slate-600">{e.severity_level}</span>
                        {e.is_incident && (
                          <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium">
                            Incident
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs max-w-[220px]">
                      <div className="truncate" title={e.notes || ''}>{e.notes || '—'}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
